import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/router';

import MakesModelsTool from '../../MakesModelsTool';
import SearchTool from '../../SearchTool';
import { SingleSelect, MinMaxSelect, MultiSelect } from '../../Selectors';
import useMinMax from '../../Hooks/MinMax';
import useBestViewHeight from '../../Hooks/BestViewHeight';

import { 
  sortOptions, fuelTypeOptions, 
  bodyTypes, bodyTypesCommercial,
  minPriceRange, maxPriceRange,
  minBatteryRange, maxBatteryRange,  
  minCargoWeightRange, maxCargoWeightRange,
  minCargoCapacityRange, maxCargoCapacityRange,
  minTowingCapacityRange, maxTowingCapacityRange,
} from '../../../helper/filters';
import { processArrayFilter } from '../../../helper/vehicle_url';
import { links, vehicleTypes } from '../../../helper/constants';

import { event } from '../../../lib/gtag';
import { vehicleFilterEvent } from '../../../utils/Analytics';

import IOption from "../../../interfaces/IOption";
import iFilters from "../../../interfaces/iFilters";

import styles from '../styles.module.scss'
import SearchIcon from '../../../public/assets/icons/search-black.svg';
import { useIsBoiWebsite } from "../../Context/WebsiteType";

interface iVehicleFilters {
  makes: IOption[],
  setMakes(makes: IOption[]): void,
  models: IOption[],
  setModels(models: IOption[]): void,
  fetchModels: boolean,
  setFetchModels(fetchModels: boolean): void,
  setRefresh(refresh: boolean): void,
  filters: iFilters,
  setFilters(filters: iFilters): void,
  cacheStatus: string,
  updateURL?: boolean,
  setUpdateURL?(updateURL: boolean): void,
  resetFilters(): void,
  handleSelectChange(value: any, name: string): void,
  commercial: boolean,
  tablet?: boolean,
  onClose?: () => void,
}

export default function NevoVehicleFilters ({
  makes, setMakes,
  models, setModels, 
  fetchModels, setFetchModels,
  filters, setFilters, cacheStatus,
  setRefresh,
  handleSelectChange,
  updateURL, setUpdateURL,
  resetFilters,
  tablet,
  commercial,
  onClose
}: iVehicleFilters) {
  const [processingURL, setProcessingURL] = useState(false);
  const router = useRouter();

  const [minPrices, maxPrices] = useMinMax(filters.price_min, filters.price_max, minPriceRange, maxPriceRange)
  const [minRanges, maxRanges] = useMinMax(filters.range_min, filters.range_max, minBatteryRange, maxBatteryRange);
  
  // commercial 
  const [minCargoWeights, maxCargoWeights] = useMinMax(filters.cargo_weight_min, filters.cargo_weight_max, minCargoWeightRange, maxCargoWeightRange);
  const [minCargoCapacities, maxCargoCapacities] = useMinMax(filters.cargo_capacity_min, filters.cargo_capacity_max, minCargoCapacityRange, maxCargoCapacityRange);
  const [minTowingCapacities, maxTowingCapacities] = useMinMax(filters.towing_capacity_min, filters.towing_capacity_max, minTowingCapacityRange, maxTowingCapacityRange);

  const isBoiWebsite = useIsBoiWebsite();

  const filtersOptions: { [key: string]: IOption[] } = useMemo(() => {
    return {
      order_by: sortOptions,
      fuel_type: fuelTypeOptions,
      body_type: commercial ? bodyTypesCommercial : bodyTypes,
      price_min: minPriceRange,
      price_max: maxPriceRange,
      range_min: minBatteryRange,
      range_max: maxBatteryRange,
      cargo_weight_min: minCargoWeightRange,
      cargo_weight_max: maxCargoWeightRange,
      cargo_capacity_min: minCargoCapacityRange,
      cargo_capacity_max: maxCargoCapacityRange,
      towing_capacity_min: minTowingCapacityRange,
      towing_capacity_max: maxTowingCapacityRange
    }
  }, [commercial])
  
  useBestViewHeight()

  useEffect(() => {
    if (cacheStatus !== "") {
      setProcessingURL(true);
    }
  }, [cacheStatus])

  const vehicleType = useMemo(() => {
    return commercial ? vehicleTypes.commercial : vehicleTypes.private
  }, [commercial])

  const {
    makeOptions,
    modelOptions,
  } = MakesModelsTool(makes, setModels, vehicleType, fetchModels, setFetchModels, filters, setRefresh)
  
  const vehicleSearch = SearchTool(makes, models, links[vehicleType], 'vehicleSearch', filters, true);

  // 5 seconds debounce analytics send
  useEffect(() => {
    const sendAnalytics = () => {
      vehicleFilterEvent(isBoiWebsite, { ...filters, makes, models })
    }

    const timeout = setTimeout(sendAnalytics, 5000);
    return () => clearTimeout(timeout);
  }, [makes, models, filters, isBoiWebsite])

  // updating url
  useEffect(() => {
    if (updateURL && setUpdateURL) {
      setUpdateURL(false);
      vehicleSearch();
      
      if (tablet) window.scrollTo(0, 0);
    }
  }, [updateURL, setUpdateURL, vehicleSearch, tablet])

  const processFiltersURL = useCallback(() => {
    if (router.query) {
      for (let parameter in router.query) {
        if (parameter === 'slug') continue
        if (filters[parameter] === undefined) continue

        let value = `${router.query[parameter]}`
        const numerical_value = parseFloat(value)

        if (Array.isArray(filters[parameter])) { // array filter case
          const array = value.split(',');
          filters[parameter] = processArrayFilter(array, filtersOptions[parameter]);
        }
        else if (numerical_value || numerical_value === 0) { // numeric filter case
          filters[parameter] = filtersOptions[parameter].find(option => {
            return option.value === numerical_value;
          });
        }
        else { // string filter case
          filters[parameter] = filtersOptions[parameter].find(option => {
            return option.value === value;
          });
        }
      }

      setFilters({...filters});
    }
  }, [router, filters, setFilters, filtersOptions])

  // Processing URL for Make, Model and Filters
  useEffect(() => {
    if (makes.length === 0 && processingURL && makeOptions.length > 0 
      && router.query.slug && router.query.slug !== "all") 
    {
      const query = router.query.slug.toString();
      let makesArray: string[];
      let refresh = cacheStatus === "not found"; 

      // If there is make and model
      if (query.includes("@")) {
        makesArray = query.split("@")[1].split(",") // getting makes from URL
        refresh = false
      }
      // only make
      else {
        makesArray = query.split(",");
        setProcessingURL(false);
      }

      const searchedMakes = processArrayFilter(makesArray, makeOptions)
      setMakes(searchedMakes);

      processFiltersURL();

      setRefresh(refresh)
    }
    else if (makes.length === 0 && processingURL && makeOptions.length > 0 
      && router.query.slug && router.query.slug === "all") {
      processFiltersURL();
      
      setProcessingURL(false);
      
      // if cache not found then refresh
      if (cacheStatus === "not found") {
        setRefresh(true);
      }

      if (setUpdateURL) setUpdateURL(true);
    }
  }, [setUpdateURL, makes, makeOptions, setMakes, processingURL, router, setRefresh, processFiltersURL, setFetchModels, cacheStatus]);

  // Processing URL for Model
  useEffect(() => {
    if (makes.length > 0 && processingURL && modelOptions.length > 0 
      && router.query.slug && router.query.slug.includes("@")) 
    {
      const query = router.query.slug.toString();
      const modelsArray: string[] = query.split("@")[0].split(',') // getting models from URL
      
      const searchedModels = processArrayFilter(modelsArray, modelOptions)
      setModels(searchedModels)
      
      // if cache not found then we can refresh
      if (cacheStatus === "not found") {
        setRefresh(true)
      }
      setProcessingURL(false);
      if (setUpdateURL) setUpdateURL(true);
    }
  }, [setUpdateURL, makes, models, modelOptions, processingURL, router, setModels, setRefresh, cacheStatus]);

  return (
    <div className={isBoiWebsite ? styles.filtersHolderBoi : styles.filtersHolder}>
      <div className={isBoiWebsite ? styles.filtersBoi : styles.filters}>
        <SingleSelect
          title="Fuel Type" 
          value={filters.fuel_type}
          placeholder="Both (BEV & PHEV)"
          options={fuelTypeOptions}
          rounded={isBoiWebsite}
          onChange={(value: any) => handleSelectChange(value, "fuel_type")}
          onClick={() => {event('clickedOnFuelTypeDropdown')}}
        />

        <MultiSelect 
          title='Make'
          values={makes}
          options={makeOptions}
          rounded={isBoiWebsite}
          onClick={() => {event('clickedOnMakeDropdown')}}
          onChange={(makes: any) => {
            let options: IOption[] = []
            Object.assign(options, makes)
            
            // clearing all selected values if all were chosen
            if (options.some(option => option.value === "all")) {
              options = []
            }
            setMakes(options)

            setRefresh(true);
            if (setUpdateURL) setUpdateURL(true);
          }}
        />

        <MultiSelect
          title='Model'
          values={models}
          options={modelOptions}
          rounded={isBoiWebsite}
          onClick={() => {event('clickedOnModelDropdown')}}
          onChange={(models: any) => {
            let options: IOption[] = []
            Object.assign(options, models)
            
            if (options.some(option => option.value === "all")) {
              options = []
            }

            setModels(options)

            setRefresh(true);
            if (setUpdateURL) setUpdateURL(true);
          }}
        />

        <MultiSelect 
          title='Body'
          values={filters.body_type}
          rounded={isBoiWebsite}
          options={commercial ? bodyTypesCommercial : bodyTypes}
          onClick={() => {event('clickedOnBodyTypeDropdown')}}
          onChange={(bodies: any) => {
            let body_type: IOption[] = []
            Object.assign(body_type, bodies)
            
            if (body_type.some(body => body.value === "all")) { // clearing all selected values if all were chosen
              body_type = []
            }
            handleSelectChange(body_type, "body_type")
          }}
        />

        {commercial && (
          <>
            <MinMaxSelect 
              title='Load Capacity'
              minPlaceholder='0m³'
              minValue={filters.cargo_capacity_min}
              minOptions={minCargoCapacities}
              onMinChange={(value: any) => handleSelectChange(value, "cargo_capacity_min")}
              maxValue={filters.cargo_capacity_max}
              maxOptions={maxCargoCapacities}
              onMaxChange={(value: any) => handleSelectChange(value, "cargo_capacity_max")}
              onMinClick={() => {event('clickedOnMinLoadCapaticyDropdown')}}
              onMaxClick={() => {event('clickedOnMaxLoadCapaticyDropdown')}}
            />

            <MinMaxSelect 
              title='Load Weight'
              minPlaceholder='0kg'
              minValue={filters.cargo_weight_min}
              minOptions={minCargoWeights}
              onMinChange={(value: any) => handleSelectChange(value, "cargo_weight_min")}
              maxValue={filters.cargo_weight_max}
              maxOptions={maxCargoWeights}
              onMaxChange={(value: any) => handleSelectChange(value, "cargo_weight_max")}
              onMinClick={() => {event('clickedOnMinLoadWeightDropdown')}}
              onMaxClick={() => {event('clickedOnMaxLoadWeightDropdown')}}
            />

            <MinMaxSelect 
              title='Towing Weight'
              minPlaceholder='0kg'
              minValue={filters.towing_capacity_min}
              minOptions={minTowingCapacities}
              onMinChange={(value: any) => handleSelectChange(value, "towing_capacity_min")}
              maxValue={filters.towing_capacity_max}
              maxOptions={maxTowingCapacities}
              onMaxChange={(value: any) => handleSelectChange(value, "towing_capacity_max")}
              onMinClick={() => {event('clickedOnMinTowingWeightDropdown')}}
              onMaxClick={() => {event('clickedOnMaxTowingWeightDropdown')}}
            />
          </>
        )}

        <MinMaxSelect 
          title='Price Range'
          minPlaceholder='€0'
          minValue={filters.price_min}
          minOptions={minPrices}
          onMinChange={(value: any) => handleSelectChange(value, "price_min")}
          maxValue={filters.price_max}
          maxOptions={maxPrices}
          onMaxChange={(value: any) => handleSelectChange(value, "price_max")}
          onMinClick={() => {event('clickedOnMinPriceDropdown')}}
          onMaxClick={() => {event('clickedOnMaxPriceDropdown')}}
        />

        <MinMaxSelect 
          title='Battery Range'
          minPlaceholder='0 km'
          minValue={filters.range_min}
          minOptions={minRanges}
          onMinChange={(value: any) => handleSelectChange(value, "range_min")}
          maxValue={filters.range_max}
          maxOptions={maxRanges}
          onMaxChange={(value: any) => handleSelectChange(value, "range_max")}
          onMinClick={() => {event('clickedOnMinRangeDropdown')}}
          onMaxClick={() => {event('clickedOnMaxRangeDropdown')}}
        />
      </div>

      <div
        className={`${isBoiWebsite ? `${styles.searchButtonBoi} light-blue-button-boi` : `${styles.searchButton} blue-button`}`}
        onClick={() => {
          event('clickedOnSearchButton')
          if (onClose) onClose()
        }}
      >
        <span className={isBoiWebsite ? styles.searchTextBoi : styles.searchText}>Search</span>
        <Image src={SearchIcon} alt="Search icon" width={18}/>
      </div>

      <p className={`${isBoiWebsite ? `${styles.resetFiltersBoi} light-blue-button-boi` : styles.resetFilters}`} onClick={() => {
        event('clickedOnResetButton')
        resetFilters()
      }}>
        Reset Filters
      </p>
    </div>
  )
};